import { useEffect, useState } from "react";
import { runtimeEnv } from "configuration";
import { packageService } from "services/package-service/PackageService";
import { globalSettingsChanged, processStatusChanged, sandboxPublicFilesSelector, serviceWorkerInit, setPreviewLoaded, transpile } from "store/features";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { Messenger } from "@sandbox/transpiler";
import { useKeyboardEventContext } from "providers/keyboard-event-provider/KeyboardEventProvider";
import { testReportGenerated } from "store/features/test/testSlice";
import { getTargetOrigin } from "utils/helpers";

interface TranspilerProps {
  children: React.ReactNode;
  iframeRef: React.RefObject<HTMLIFrameElement>
}

export const Transpiler = ({ iframeRef, children }: TranspilerProps) => {
  const selectedImodel = useAppSelector((state) => state.activeSandbox.selectedImodel);
  const files = useAppSelector(sandboxPublicFilesSelector);
  const transpilerInitialized = useAppSelector((state) => state.transpiler.esbuildInitialized);
  const transpileStatus = useAppSelector((state) => state.transpiler.status);
  const previewLoaded = useAppSelector((state) => state.transpiler.previewLoaded);
  const buildResult = useAppSelector((state) => state.transpiler.buildResult);
  const testBuildResults = useAppSelector((state) => state.transpiler.testBuildResults);
  const packages = useAppSelector((state) => state.activeSandbox.resolvedPackages);
  const importmap = useAppSelector((state) => state.activeSandbox.importmap);
  const sandboxResolvedPackages = useAppSelector((state) => state.activeSandbox.resolvedPackages);
  const itwinjsVersion = useAppSelector((state) => state.activeSandbox.itwinjsVersion);
  const [previewPrepared, setPreviewPrepared] = useState(false);
  const dispatch = useAppDispatch();

  const { registerEvent } = useKeyboardEventContext();

  Messenger.onErrorReveal = ({ fileName, lineNumber }) => {
    console.log("onErrorReveal", fileName, lineNumber);
    dispatch(globalSettingsChanged({ filePath: [fileName], startLineNumber: lineNumber }))
  };

  // Set Preview on Transpile started
  useEffect(() => {
    if (iframeRef.current && transpileStatus === "building") {
      dispatch(setPreviewLoaded(false));
      iframeRef.current.src = getIframeUrl(selectedImodel ?? {});
      setPreviewPrepared(false);
    }
  }, [iframeRef.current, transpileStatus]);

  // When preview is loaded send there dependency packages
  useEffect(() => {
    if (!transpilerInitialized || !previewLoaded || !packages)
      return;

    if (!iframeRef.current?.contentWindow)
      return;

    const messenger = new Messenger(iframeRef.current.contentWindow, getTargetOrigin());

    const publicFiles = files.reduce((prev, curr) => {
      const filePath = curr.filePath.slice(7);
      prev[filePath] = curr.content ?? "";
      return prev;
    }, {} as Record<string, string>);

    messenger.initialize()
      .then(() => dispatch(serviceWorkerInit(true)))
      .then(() => {
        return messenger.setPublicFiles(publicFiles)
      })
      .then(() => {
        return messenger.addPackagesAndImportMap(packageService.getScriptPaths(packages), importmap ? importmap : packageService.getImportmapPaths(packages))
      })
      .then(() => setPreviewPrepared(true))
      .catch((error) => console.error(error));

  }, [transpilerInitialized, packages?.length, iframeRef.current, previewLoaded]);

  // When preview iframe is loaded and transpile is completed, send bundle to iframe
  useEffect(() => {
    if (!buildResult || !previewLoaded || transpileStatus === "building" || !previewPrepared)
      return;

    if (!iframeRef.current?.contentWindow)
      return;

    const messenger = new Messenger(iframeRef.current.contentWindow, getTargetOrigin());

    if (buildResult.content) {
      dispatch(processStatusChanged("loading-dependencies"));
      messenger.addSandboxScript("sandbox-bundle", buildResult.content, true).finally(() => {
        dispatch(processStatusChanged("idle"));
      });
    } else {
      //https://github.com/facebook/create-react-app/blob/d960b9e38c062584ff6cfb1a70e1512509a966e7/packages/react-error-overlay/src/utils/parseCompileError.js#L25
      if (buildResult.errors && buildResult.errors.length) {
        const message = buildResult.errors.map((error) => {
          return error.suggestion ? `${error.text}\n${error.suggestion}` : error.text;
        }).join("\n\n");

        if (buildResult.errors[0].file && buildResult.errors[0].line && buildResult.errors[0].column) {
          messenger.reportBuildError(`${message}\n\n./${buildResult.errors[0].file}\nLine ${buildResult.errors[0].line}:${buildResult.errors[0].column}\n`)
        } else {
          messenger.reportBuildError(message);
        }
      } else {
        messenger.reportBuildError("Unexpected build error. See console log of browsers' DevTools for more information");
      }
      dispatch(processStatusChanged("idle"));
    }
  }, [iframeRef, buildResult, previewLoaded, transpileStatus, previewPrepared]);


  useEffect(() => {
    if (!iframeRef.current?.contentWindow)
      return;

    if (!testBuildResults || !previewLoaded || !previewPrepared)
      return;

    const messenger = new Messenger(iframeRef.current.contentWindow, getTargetOrigin());
    const pendingScripts = [];

    for (const buildResult of testBuildResults) {
      if (buildResult.content) {
        const result = messenger.addSandboxScript(`test-bundle-${pendingScripts.length}`, buildResult.content, true);
        pendingScripts.push(result);
      }
    }

    Promise.all(pendingScripts)
      .then(() => messenger.runTests())
      .then((report) => dispatch(testReportGenerated(report as any)))
      .catch((error) => console.error(error));
  }, [iframeRef, previewLoaded, testBuildResults, previewPrepared]);

  // Dispatch a transpile when selectedImodel is changed
  useEffect(() => {
    if (sandboxResolvedPackages && sandboxResolvedPackages.length && itwinjsVersion !== "2.x")
      dispatch(transpile());
  }, [selectedImodel?.iModelId, sandboxResolvedPackages?.length, itwinjsVersion])

  useEffect(() => {
    const unregister = registerEvent({ ctrlKey: true, key: "b" }, () => dispatch(transpile()));
    return () => {
      unregister();
    };
  }, [registerEvent]);

  return (
    <>
      {children}
    </>
  )
};

function getIframeUrl({ iTwinId, iModelId }: { iTwinId?: string, iModelId?: string }) {
  const params: { [key: string]: string } = {};

  params.lng = "en";
  params.baseUrl = encodeURIComponent(runtimeEnv.REACT_APP_BASEURL);
  iTwinId && (params.itwinid = iTwinId);
  iModelId && (params.imodelid = iModelId);

  const queryString = new URLSearchParams(params).toString();
  return `${runtimeEnv.REACT_APP_COMPILER_URL}?${queryString}`;
}
